import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config';
import OptionIcon from '../../assets/option.png';
import Swal from 'sweetalert2';
import './OrganizationSettings.css';
import loadingGif from '../../assets/loading.gif';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import { Button, Avatar, CircularProgress } from "@mui/material";
import OrgLogoUploader from "./OrgLogoUploader";
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Switch, 
  FormControlLabel, 
  Alert, 
  AlertTitle,
  LinearProgress 
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import InfoIcon from '@mui/icons-material/Info';




const OrganizationSettings = () => {
  const [organizations, setOrganizations] = useState([]);
  const [orgPermissions, setOrgPermissions] = useState({});
  const [isEnabled, setIsEnabled] = useState(null);
  const [deleteOrgDialogOpen, setDeleteOrgDialogOpen] = useState(false);
  const [deleteOrgConfirmName, setDeleteOrgConfirmName] = useState("");
  const [selectedDeleteOrg, setSelectedDeleteOrg] = useState(null);
  const [emailSettings, setEmailSettings] = useState({});

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token');

      if (!token) {
        navigate('/unauthorized');
        return;
      }

      setLoading(true);

      try {
        const orgResponse = await fetch(`${API_BASE_URL}/org/organization-settings/all-organizations`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (orgResponse.status === 403) {
          navigate('/unauthorized');
          return;
        }

        if (!orgResponse.ok) {
          throw new Error('Network response was not ok');
        }

        const orgData = await orgResponse.json();

        const filteredOrgs = await Promise.all(orgData.map(async (org) => {
          const isEnabled = await checkMemberEnabled(org.orgId);
          return isEnabled ? org : null;
        }));

        setOrganizations(filteredOrgs.filter(org => org));

        for (let org of filteredOrgs) {
          await fetchPermissionsForOrg(org.orgId);
        }

        const adminResponse = await fetch(`${API_BASE_URL}/users/admin`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!adminResponse.ok) {
          throw new Error('Failed to fetch admin status');
        }

        const adminData = await adminResponse.json();
        setIsEnabled(adminData.isEnabled);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchEmailSettings = async () => {
      const token = localStorage.getItem('token');

      try {
        const response = await axios.get(`${API_BASE_URL}/org/organization-settings/email-settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data) {
          setEmailSettings(response.data);
        }
      } catch (error) {
        console.error(`Error fetching email settings:`, error);
      }
    };

    const checkMemberEnabled = async (orgId) => {
      const response = await fetch(`${API_BASE_URL}/org-members/member-enabled`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          SelectedOrg: orgId,
        },
      });

      return response.ok && response.status !== 403;
    };

    const fetchPermissionsForOrg = async (orgId) => {
      const token = localStorage.getItem('token');

      try {
        const response = await axios.get(`${API_BASE_URL}/org/permissions`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { orgId: orgId },
        });

        if (response.data) {
          setOrgPermissions(prevPermissions => ({
            ...prevPermissions,
            [orgId]: response.data,
          }));
        }
      } catch (error) {
        console.error(`Error fetching permissions for org ${orgId}:`, error);
      }
    };

    fetchData();
    fetchEmailSettings();
  }, [navigate]);

  const handleCreateOrganization = () => {
    navigate('/organization/settings/create');
  };

  const handleEmailLimitToggle = async (checked) => {
    try {
      // Update the email settings via API
      const response = await fetch(`${API_BASE_URL}/org/organization-settings/email-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          exceedEmailLimit: checked
        })
      });
      
      if (response.ok) {
        setEmailSettings(prev => ({
          ...prev,
          exceed_email_limit: checked
        }));
      }
    } catch (error) {
      console.error('Error updating email settings:', error);
    }
  };

  const handleOpenDeleteOrgDialog = (org) => {
    setSelectedDeleteOrg(org);
    setDeleteOrgConfirmName("");
    setDeleteOrgDialogOpen(true);
  };

  const handleDeleteOrgCancel = () => {
    setDeleteOrgDialogOpen(false);
    setSelectedDeleteOrg(null);
    setDeleteOrgConfirmName("");
  };

  const handleDeleteOrgConfirm = async () => {
    if (!selectedDeleteOrg) return;
    try {
      const response = await axios.post(`${API_BASE_URL}/org/delete`, null, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          SelectedOrg: selectedDeleteOrg.orgId,
        },
      });
      if (response.status === 201) {
        Swal.fire('Deleted!', `${selectedDeleteOrg.orgName} has been deleted.`, 'success');
        setOrganizations(organizations.filter(org => org.orgId !== selectedDeleteOrg.orgId));
        window.location.reload();
      } else {
        throw new Error('Failed to delete organization');
      }
    } catch (error) {
      Swal.fire('Error', 'Failed to delete the organization. Please try again.', 'error');
    }
    handleDeleteOrgCancel();
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 'calc(100vh - 64px)',
        backgroundColor: '#fff',
      }}>
        <img src={loadingGif} alt="Loading..." style={{ width: '30%', height: '30%', objectFit: 'contain' }} />
      </div>
    );
  }

  return (
    <div className="organizations-container">
      <div className="organizations-header">
        <h1>Organizations</h1>
        {isEnabled === 1 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateOrganization}
              sx={{ textTransform: 'none', fontWeight: 600 }}
              disabled={!Object.values(orgPermissions).some(perm => perm.org_settings_create === 1)}
            >
              Create Organization
            </Button>
          </div>
        )}
        {emailSettings && emailSettings.max_email > 0 && (
          <Card sx={{ mb: 3, p: 3, bgcolor: 'background.paper' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Email Settings
              </Typography>
              
              {/* Monthly limit display */}
              <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body1" gutterBottom>
                    Monthly Email Usage: <strong>{emailSettings.current_month_usage.toLocaleString()}/{emailSettings.max_email.toLocaleString()}</strong> emails
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min((emailSettings.current_month_usage / emailSettings.max_email) * 100, 100)}
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: (emailSettings.current_month_usage / emailSettings.max_email) > 0.8 ? 'error.main' : 'primary.main'
                      }
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {Math.round((emailSettings.current_month_usage / emailSettings.max_email) * 100)}% used this month
                    {emailSettings.current_month_usage >= emailSettings.max_email && (
                      <span style={{ color: '#d32f2f', fontWeight: 'bold', marginLeft: 8 }}>
                        ⚠️ Limit exceeded
                      </span>
                    )}
                  </Typography>
                </Box>
              
              {/* Switch for exceed limit */}
              <FormControlLabel
                control={
                  <Switch
                    checked={emailSettings.exceed_email_limit || false}
                    onChange={(e) => handleEmailLimitToggle(e.target.checked)}
                    color="primary"
                  />
                }
                label="Allow exceeding monthly email limit"
              />
              
              {/* Explanation */}
              <Alert severity="info" sx={{ mt: 2 }} icon={<InfoIcon />}>
                <AlertTitle>How it works</AlertTitle>
                When enabled, the system will warn you when approaching the monthly email limit 
                but will continue sending emails. Additional charges may apply for emails sent 
                beyond your monthly allowance. 
              </Alert>
            </CardContent>
          </Card>
        )}
      </div>
      <div className="org-card-grid">

       {organizations.map(org => (
        <div className="org-card" key={org.orgId}>
          <div className="org-card-header" alignItems="left">
            <span className="org-name">{org.orgName}</span>
            <OrgLogoUploader org={org} token={token} API_BASE_URL={API_BASE_URL} />
              {orgPermissions[org.orgId]?.org_settings_update ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>{org.isEnabled ? 'Enabled' : 'Disabled'}</span>
                  <Switch
                    checked={org.isEnabled}
                    onChange={async (e) => {
                      const newEnabled = e.target.checked ? 1 : 0;
                      try {
                        await fetch(`${API_BASE_URL}/org/update-org-enabled`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'selectedorg': org.orgId,
                            'Authorization': `Bearer ${token}`,
                          },
                          body: JSON.stringify({ isEnabled: newEnabled })
                        });
                        window.location.reload();
                      } catch (err) {
                        Swal.fire('Error', 'Failed to update organization status. Please try again.', 'error');
                      }
                    }}
                    color="primary"
                  />
                </div>
              ) : (
                  <span className={`org-status-chip ${org.isEnabled ? 'enabled' : 'disabled'}`}>{org.isEnabled ? 'Enabled' : 'Disabled'}</span>

               )}
            </div>
            <div className="org-card-body">
              <div className="org-info">{org.orgEmail}</div>
              <div className="org-info">{org.orgNumber}</div>
            </div>
            <div className="org-card-actions">
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={orgPermissions[org.orgId]?.org_settings_update !== 1 || !org.isEnabled}
                onClick={() => window.location.hash = `/organization/settings/org-settings/${org.orgId}`}
              >
                Edit Details
              </Button>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                disabled={orgPermissions[org.orgId]?.org_settings_create !== 1 || !org.isEnabled}
                onClick={() => window.location.hash = `/organization/settings/create-job-flow/${org.orgId}`}
              >
                Create Job Flow
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                disabled={orgPermissions[org.orgId]?.org_settings_update !== 1 || !org.isEnabled}
                onClick={() => window.location.hash = `/organization/settings/job-flow-settings/${org.orgId}`}
              >
                Job Flow Settings
              </Button>
              <Button
                variant="contained"
                color="error"
                size="small"
                disabled={orgPermissions[org.orgId]?.org_settings_delete !== 1 || !org.isEnabled}
                onClick={() => handleOpenDeleteOrgDialog(org)}
              >
                Delete
              </Button>
            </div>
          </div>
        ))}
      </div>
      <Dialog
        open={deleteOrgDialogOpen}
        onClose={handleDeleteOrgCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{ color: 'black' }}>Delete Organization</DialogTitle>
        <DialogContent>
          <div style={{ marginBottom: 16 }}>
            Please type <b>{selectedDeleteOrg?.orgName}</b> to confirm deletion.
          </div>
          <TextField
            label="Type the organization name to confirm"
            variant="outlined"
            multiline
            rows={1}
            fullWidth
            value={deleteOrgConfirmName}
            onChange={e => setDeleteOrgConfirmName(e.target.value)}
            autoFocus
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteOrgCancel} style={{ padding: '6px 12px' }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteOrgConfirm}
            variant="contained"
            color="error"
            style={{ padding: '6px 12px' }}
            disabled={
              !selectedDeleteOrg ||
              !deleteOrgConfirmName ||
              (selectedDeleteOrg?.orgName !== deleteOrgConfirmName)
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default OrganizationSettings;
